import { createAsyncThunk } from '@reduxjs/toolkit'
import { SOMETHING_WHEN_WRONG } from 'constant/errorMessages'
import {
  getInventoryDetailsGroupsAPI,
  GetInventoryDetailsGroupsParamsType,
  GetSOBusinessTaggingConfigResponseType,
  getSOBusinessTaggingConfigAPI,
  GetSOProductsRequestType,
  GetSOProductsResponseType,
  InventoryDetailGroupType,
  PostSupplyOrderDetailRequestType,
  postSupplyOrderDetailAPI,
  PutSupplyOrderDetailRequestType,
  putSupplyOrderDetailAPI,
} from 'features/SupplyOrder/services/outboundSO'
import { callErrorMsg } from 'helpers/errorMsg'

export const SLICE_NAME = 'OUTBOUND_SO_ADD_PRODUCT'

// The picker migrated from GET /api/supply-order-grand/product/{locationId}, which maps to the
// inventory-detail grain (qr_code + expiry_date populated, FEFO sort). TODO: confirm with BE.
const IS_GROUP = true

// Maps a new inventory-details group row back to the legacy Add Product table row shape, so the
// slice / Table / Row components need no change.
const mapSOProduct = (
  group: InventoryDetailGroupType,
): GetSOProductsResponseType['data']['content'][number] => ({
  productDetailId: Number(group.inventory_detail_id),
  productExpiryDate: group.expiry_date || null,
  productId: Number(group.product.id),
  productImage: group.product.image || null,
  productInventoryNote: group.status_note?.name || null,
  productInventoryNoteId: group.status_note?.id ? Number(group.status_note.id) : null,
  productInventoryStatus: group.status?.name ?? '',
  productInventoryStatusId: group.status?.id ?? 0,
  productName: group.product.name,
  productQrCode: group.qr_code || '',
  productRackId: null,
  productRackName: null,
  productSku: group.product.sku_number,
  productStock: group.total_stock,
  productUniqueKey: `${group.inventory_detail_id}-${group.qr_code}-${group.expiry_date}`,
})

export const fetchGetSOBusinessTaggingCongig = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSOBusinessTaggingCongig`,
  async (
    props: {
      destinationId: number
      onSuccess?: (data: GetSOBusinessTaggingConfigResponseType['data']) => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { destinationId, onSuccess } = props

      const res = await getSOBusinessTaggingConfigAPI(destinationId)

      onSuccess?.(res.data.data)

      return res.data.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetSOProduct = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSOProduct`,
  async (
    props: {
      locationId: number
      params?: GetSOProductsRequestType
    },
    { rejectWithValue, getState },
  ) => {
    try {
      const { locationId, params } = props
      const {
        outboundSOAddProduct: { query },
      } = getState() as StoreStateType

      const merged = { ...query, ...params }
      const isReturn = merged.soType?.toLowerCase() === 'return'

      const apiParams: GetInventoryDetailsGroupsParamsType = {
        location_ids: [locationId],
        search_product: merged.name || undefined,
        // BE requires the PO scope for return SOs; other SO types stay location-scoped.
        purchase_order_id: isReturn ? merged.purchaseOrderId : undefined,
        is_group: IS_GROUP,
        /* eslint-disable @typescript-eslint/naming-convention */
        // Legacy UI state keeps pageIndex 0-based; this endpoint's page index starts at 1.
        'pagination.page_index': (merged.pageIndex ?? 0) + 1,
        'pagination.page_size': merged.pageSize,
        /* eslint-enable @typescript-eslint/naming-convention */
      }

      const res = await getInventoryDetailsGroupsAPI(apiParams)

      const content = res.data.data.map(mapSOProduct)

      const result: GetSOProductsResponseType = {
        data: { content },
      }

      return result
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchPutSupplyOrderDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchPutSupplyOrderDetail`,
  async (
    props: {
      supplyOrderId: number
      payload: PutSupplyOrderDetailRequestType
      onSuccess?: () => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { supplyOrderId, payload, onSuccess } = props
      const response = await putSupplyOrderDetailAPI(supplyOrderId, payload)

      onSuccess?.()

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchPostSupplyOrderDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchPostSupplyOrderDetail`,
  async (
    props: {
      payload: PostSupplyOrderDetailRequestType
      onSuccess?: (supplyOrderId: number) => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { payload, onSuccess } = props
      const response = await postSupplyOrderDetailAPI(payload)

      onSuccess?.(response.data.data.supplyOrderId)

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)
